import { SectionHeader } from "@/components/RevenueEngine";
import { Check, X } from "@phosphor-icons/react";
import Logo from "@/components/Logo";

const ROWS = [
  {
    trait: "Delivery model",
    them: "Retainer + hours logged",
    us: "A system that runs in your stack",
  },
  {
    trait: "Core asset",
    them: "Their agency playbook",
    us: "Infrastructure you own, not rent",
  },
  {
    trait: "Optimization target",
    them: "Impressions & open rates",
    us: "Closed-won pipeline",
  },
  {
    trait: "What happens if you stop paying",
    them: "Everything stops",
    us: "Your system keeps running",
  },
  {
    trait: "Engagement model",
    them: "Campaigns",
    us: "Owned pipeline engine",
  },
  {
    trait: "Risk",
    them: "You pay up front",
    us: "We don't deliver, you don't pay",
  },
];

export default function Differentiation() {
  return (
    <section
      id="differentiation"
      data-testid="differentiation"
      className="relative bg-[#050505] border-t border-white/10 py-28 md:py-40"
    >
      <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14">
        <SectionHeader
          overline="Position / 07"
          title={<>We engineer systems.<br /><span className="text-[#D946EF]">Not campaigns.</span></>}
          sub="The honest comparison. No diplomatic language. Your buying committee will appreciate it."
        />

        <div className="mt-16 grid grid-cols-12 gap-0 border border-white/10">
          <div className="col-span-12 md:col-span-4 p-6 border-b border-white/10 font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">
            Trait
          </div>
          <div className="col-span-6 md:col-span-4 p-6 border-l border-b border-white/10">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-1">Traditional</div>
            <div className="font-display font-bold text-zinc-400 uppercase tracking-tighter">Agencies</div>
          </div>
          <div className="col-span-6 md:col-span-4 p-6 border-l border-b border-white/10 relative">
            <div className="absolute left-0 top-0 bottom-0 w-[3px] bg-[#CCFF00]" style={{ boxShadow: "0 0 20px #CCFF00" }} />
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#CCFF00] mb-2">Our approach</div>
            <Logo />
          </div>

          {ROWS.map((r) => (
            <DiffRow key={r.trait} row={r} />
          ))}
        </div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { k: "We don't chase pipeline.", v: "We build systems that generate it.", c: "#00F0FF" },
            { k: "We don't guess attribution.", v: "We know what drives revenue.", c: "#D946EF" },
            { k: "We don't chase volume.", v: "We compound precision.", c: "#CCFF00" },
          ].map((s) => (
            <div
              key={s.k}
              className="p-8 border border-white/10 bg-white/[0.02] relative"
              style={{ boxShadow: `inset 0 1px 0 0 ${s.c}22` }}
            >
              <div className="font-display font-bold uppercase tracking-tighter text-xl md:text-2xl leading-tight" style={{ color: s.c }}>
                {s.k}
              </div>
              <div className="mt-2 font-display font-bold uppercase tracking-tighter text-xl md:text-2xl text-white leading-tight">
                {s.v}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function DiffRow({ row }) {
  return (
    <>
      <div className="col-span-12 md:col-span-4 p-5 border-b border-white/10 font-mono text-[11px] uppercase tracking-wider text-zinc-300">
        {row.trait}
      </div>
      <div className="col-span-6 md:col-span-4 p-5 border-l border-b border-white/10 flex items-center gap-3 font-body text-[14px] text-zinc-500">
        <X size={14} weight="bold" className="text-zinc-600 shrink-0" />
        <span className="line-through decoration-white/40">{row.them}</span>
      </div>
      <div className="col-span-6 md:col-span-4 p-5 border-l border-b border-white/10 flex items-center gap-3 font-body text-[14px] text-white bg-[#CCFF00]/[0.02]">
        <Check size={14} weight="bold" className="text-[#CCFF00] shrink-0" />
        <span>{row.us}</span>
      </div>
    </>
  );
}
